/**
 * `SfxEngine` — the one seam between the app and its sound source.
 *
 * The sound source is **procedural synthesis, not audio files**: a home-grown Web Audio synth
 * (`./synth.ts`). Call sites never import the synth directly — they call this singleton's
 * trigger-named methods (`click()`, `impact()`, `miss()`, …), each of which dispatches
 * to a `synth.*()` call. That indirection is deliberate: a later non-synth sound source (a
 * sampled file, a different synth lib) becomes a new branch inside an existing method here, not a
 * refactor of every call site across the app.
 *
 * **Autoplay policy:** browsers gate `AudioContext` behind a user gesture, so the context is
 * never created at module load or at boot. What IS done at boot is arming the first-gesture
 * unlock listener — {@link SfxEngine.prime}, called once from `main.tsx`. The context then comes
 * up on the first gesture *anywhere* in the app (a login click), not on the first sound.
 * `prime`'s own doc carries the measurement showing why arming it lazily, from the first trigger,
 * is too late to hear the gesture that triggered it.
 *
 * **Fire-and-forget:** every trigger method swallows its own errors. Some call sites (the
 * `PresentationDirector`'s beat queue) are `Promise`-sequenced and must never be blocked or
 * failed by a sound call.
 */
import { storageGet, storageSet } from "../browserStorage";
import { synth } from "./synth";

const MUTED_KEY = "lvtt.sfx.muted";
const VOLUME_KEY = "lvtt.sfx.volume";
const DEFAULT_VOLUME = 1;

/** Read the persisted mute flag, or `false` (unmuted) if unset/unavailable — sound is opt-out. */
function readMuted(): boolean {
  const stored = storageGet(MUTED_KEY);
  return stored.ok && stored.value === "true";
}

/** Persist the mute flag (storage failures are swallowed — in-session mute still holds). */
function writeMuted(muted: boolean): void {
  // A failed write is swallowed: storage may be unavailable (private mode) and mute
  // still works in-session.
  storageSet(MUTED_KEY, muted ? "true" : "false");
}

/** Read the persisted volume (0-1), or the default if unset/unavailable/malformed. */
function readVolume(): number {
  const stored = storageGet(VOLUME_KEY);
  if (!stored.ok || stored.value === undefined) return DEFAULT_VOLUME;
  const v = Number(stored.value);
  return Number.isFinite(v) ? v : DEFAULT_VOLUME;
}

/** Persist the volume (storage failures are swallowed — in-session volume still holds). */
function writeVolume(volume: number): void {
  storageSet(VOLUME_KEY, String(volume));
}

class SfxEngine {
  private initialized = false;
  private muted: boolean = readMuted();
  private volume: number = readVolume();

  /**
   * **The class's single `try`** — every Web Audio call in this file goes through here.
   *
   * Web Audio genuinely throws: no `AudioContext` in the realm, a locked-down or headless
   * environment, a browser that refuses a buffer source. A sound must never throw into or block
   * its caller (see the class doc's fire-and-forget note), so every such failure is swallowed —
   * and swallowed in exactly one place, rather than at each `synth.*` call site.
   *
   * **This is a channelling helper, not a lifecycle one.** It does NOT initialize; it is
   * deliberately orthogonal to {@link ensureInit} and to the `this.initialized` guards on
   * mute/volume, so that routing a call through it can never move when or whether the
   * `AudioContext` comes up. Callers keep their own init decision and pass only the call.
   */
  private swallow(call: () => void): void {
    try {
      call();
    } catch {
      // fire-and-forget — see this method's doc
    }
  }

  /**
   * Arm the synth: install its reduced-motion, lifecycle and **first-gesture-unlock** listeners.
   * Creates no `AudioContext` — `synth.init()` only binds listeners, and the context is
   * constructed inside the gesture handler, which is the only legal moment (see `prime`).
   */
  private ensureInit(): void {
    if (this.initialized) return;
    this.initialized = true;
    // Bring-up failure (missing AudioContext, locked-down environment, …) must not break the caller.
    this.swallow(() => synth.init({ muted: this.muted, volume: this.volume }));
  }

  /**
   * Arm the first-gesture audio unlock **at boot** — call once from `main.tsx`.
   *
   * **Why boot and not lazily, measured rather than reasoned.**
   * `synth.bindGestureUnlock()` — the one-shot
   * `pointerdown`/`touchstart`/`mousedown`/`keydown` listener that brings the context up inside a
   * real user gesture — is only reachable through `synth.init()` ← {@link ensureInit} ←
   * {@link fire}. Arming it there means arming it *while handling the first sound*, and that
   * first sound is almost always a click dispatched by `uiFeedback`'s delegated `click`
   * listener — which fires AFTER that same gesture's `pointerdown`/`mousedown`. The unlock
   * listener is then installed a few microseconds too late to hear the gesture that installed
   * it, and `play()` finds `ctx === null` and returns silently.
   *
   * An e2e probe wrapping `window.AudioContext` measured exactly that: **0 contexts at boot,
   * 0 after the first real click, 1 after the second** — the user's first click was silent and
   * audio stayed dead until they clicked again. Priming at boot instead makes the login click
   * the gesture that brings the context up, long before any sound is wanted.
   *
   * **This does NOT create an `AudioContext` at boot** — autoplay policy makes that impossible.
   * Adding an event listener is not creating a context, and the construction still happens
   * inside the gesture handler, where it is legal.
   */
  prime(): void {
    this.ensureInit();
  }

  /** Run a synth call after lazy init, swallowing any error — a sound must never throw or block. */
  private fire(play: () => void): void {
    this.ensureInit();
    this.swallow(play);
  }

  // --- trigger-named public API — call sites use these, never `synth` directly ---

  click(): void {
    this.fire(() => synth.click());
  }

  /** A weapon attack connecting (`presentationDirector.ts`'s `impact` beat). */
  impact(): void {
    this.fire(() => synth.error());
  }

  /** A weapon attack whiffing (`presentationDirector.ts`'s `miss` beat). */
  miss(): void {
    this.fire(() => synth.swoosh());
  }

  // --- mute / volume, persisted to localStorage ---

  // The `this.initialized` guard on each of these is deliberate and is NOT what `swallow` does:
  // mute/volume must not be the thing that brings the synth up, so an un-inited engine records the
  // new value and stops there. `ensureInit` stays the only path to `synth.init()`.

  mute(): void {
    this.muted = true;
    writeMuted(true);
    if (this.initialized) this.swallow(() => synth.mute());
  }

  unmute(): void {
    this.muted = false;
    writeMuted(false);
    if (this.initialized) this.swallow(() => synth.unmute());
  }

  isMuted(): boolean {
    return this.muted;
  }

  setVolume(v: number): void {
    this.volume = v;
    writeVolume(v);
    if (this.initialized) this.swallow(() => synth.setVolume(v));
  }
}

/** The one `SfxEngine` instance — import this, never construct a second one. */
export const sfxEngine = new SfxEngine();
